import {useEffect, useRef} from 'react';
import {useLocation, useNavigationType} from 'react-router-dom';

export const MAIN_CONTENT_ID = 'mainContent';

// scrollTop per history entry key, so back/forward can return the user to where
// they were rather than the top of the page.
const savedPositions = new Map<string, number>();

// Lazily-loaded pages mount before their content is tall enough to scroll into,
// so a restore is retried over a few frames until the position sticks.
const RESTORE_ATTEMPTS = 20;

/**
 * The app scrolls inside the content panel (see .contentPanel), not the window,
 * so React Router navigations keep the previous scroll position. On a new
 * navigation, reset to the top — or to the hash anchor when the URL has one. On
 * a history pop (back/forward), restore the position saved for that entry.
 */
function ScrollToTop() {
    const {key, hash} = useLocation();
    const navigationType = useNavigationType();
    const isRestoringRef = useRef(false);

    // Track the live scroll position for the current history entry.
    useEffect(() => {
        const panel = document.getElementById(MAIN_CONTENT_ID);
        if (!panel) return;

        const onScroll = () => {
            // Ignore the programmatic scrolls a restore makes, which would
            // otherwise overwrite the position being restored to.
            if (isRestoringRef.current) return;
            savedPositions.set(key, panel.scrollTop);
        };

        panel.addEventListener('scroll', onScroll, {passive: true});
        return () => panel.removeEventListener('scroll', onScroll);
    }, [key]);

    useEffect(() => {
        const panel = document.getElementById(MAIN_CONTENT_ID);
        if (!panel) return;

        if (hash) {
            const target = document.getElementById(hash.slice(1));
            if (target) {
                target.scrollIntoView({block: 'start'});
                return;
            }
        }

        const top =
            navigationType === 'POP' ? (savedPositions.get(key) ?? 0) : 0;

        if (top === 0) {
            panel.scrollTo({top: 0, left: 0});
            window.scrollTo({top: 0, left: 0});
            return;
        }

        let frame = 0;
        let attempts = 0;
        isRestoringRef.current = true;

        // A real user scroll wins over a restore that is still settling.
        const abort = () => {
            isRestoringRef.current = false;
            cancelAnimationFrame(frame);
        };
        panel.addEventListener('wheel', abort, {passive: true, once: true});
        panel.addEventListener('touchstart', abort, {
            passive: true,
            once: true
        });

        const restore = () => {
            if (!isRestoringRef.current) return;

            panel.scrollTop = top;
            attempts += 1;

            if (
                Math.abs(panel.scrollTop - top) > 1 &&
                attempts < RESTORE_ATTEMPTS
            ) {
                frame = requestAnimationFrame(restore);
                return;
            }

            isRestoringRef.current = false;
        };
        restore();

        return () => {
            isRestoringRef.current = false;
            cancelAnimationFrame(frame);
            panel.removeEventListener('wheel', abort);
            panel.removeEventListener('touchstart', abort);
        };
    }, [key, hash, navigationType]);

    return null;
}

export default ScrollToTop;
